import { AnalyticsBrowser, AnalyticsBrowserSettings, InitOptions } from '@segment/analytics-next';

import {
  createDQC01Traits,
  createTQC03Properties,
  createTQC04Properties,
  createTQC06Properties,
} from './segment-trait-mappers';
import { AnalyticsData, CreditTerms, Offer } from '../../domain/qualification';
import { getCookie } from '../../general/utils/cookies';
import { RootState } from '../../store';

const analytics = new AnalyticsBrowser();
let loadState: 'pending' | 'loaded' | 'disabled' = 'pending';
const isDisabled = () => loadState === 'disabled';

export type Traits = ReturnType<typeof createDQC01Traits> | { email: string };

type Context = {
  coastMsgType: 'DQC09' | 'DQC01' | 'TQC03' | 'TQC04' | 'TQC05' | 'TQC06';
};
type Options = { context?: Context };

const getCookieProperties = () => ({
  statsig_stable_id: getCookie('statsig_stable_id'),
  hubspotutk: getCookie('hubspotutk'),
});

const identify = async ({ userId, traits, options = {} }: { userId?: string; traits: Traits; options?: Options }) => {
  if (isDisabled()) return;
  await clearUserTraits();
  await analytics.identify(userId, { ...traits, ...getCookieProperties() }, options);
};

const clearUserTraits = async () => {
  const user = await analytics.user();
  return user.traits({});
};

// Do NOT call more than once (https://github.com/segmentio/analytics-next/tree/master/packages/browser#lazy--delayed-loading)
const loadAnalytics = (settings: AnalyticsBrowserSettings, options: InitOptions = {}) => {
  if (!settings.writeKey) {
    loadState = 'disabled';
    return;
  }

  analytics.load(settings, options);
  loadState = 'loaded';
};

const resetAnalytics = async () => analytics.reset();

const sendDQC01 = async ({ analyticsData, state }: { analyticsData: AnalyticsData; state: RootState }) => {
  const traits = createDQC01Traits({ analyticsData, state });
  const options: Options = {
    context: { coastMsgType: 'DQC01' },
  };
  return identify({ userId: analyticsData.userId, traits, options });
};

const sendTQC05 = (paylaod: { testimonialPanelExperiment: string }) => {
  if (isDisabled()) return;
  const properties = { ...paylaod, ...getCookieProperties() };
  const options: Options = {
    context: { coastMsgType: 'TQC05' },
  };

  return analytics.track('Get Started', properties, options);
};

const sendTQC03 = (state: RootState) => {
  if (isDisabled()) return;
  const properties = { ...createTQC03Properties(state), ...getCookieProperties() };
  const options: Options = {
    context: { coastMsgType: 'TQC03' },
  };

  return analytics.track('Business Info 2 Submitted', properties, options);
};

const sendTQC04 = ({
  state,
  creditTerms,
  offers,
}: {
  state: RootState;
  creditTerms: Pick<CreditTerms, 'billingCycle' | 'finalCreditLimit'>;
  offers?: Offer[];
}) => {
  if (isDisabled()) return;
  const properties = {
    ...createTQC04Properties({ creditTerms, state, offers }),
    ...getCookieProperties(),
  };
  const options: Options = {
    context: { coastMsgType: 'TQC04' },
  };

  return analytics.track('Application Prequalified', properties, options);
};

const sendTQC06 = (offerSelected: number) => {
  if (isDisabled()) return;
  const properties = {
    ...createTQC06Properties(offerSelected),
    ...getCookieProperties(),
  };
  const options: Options = {
    context: { coastMsgType: 'TQC06' },
  };

  return analytics.track('Offer Selected', properties, options);
};

const sendPage = () => {
  if (isDisabled()) return;
  return analytics.page(undefined, undefined, getCookieProperties());
};

export { loadAnalytics, resetAnalytics, sendDQC01, sendTQC03, sendTQC04, sendTQC05, sendTQC06, sendPage };
